/**
 * diarizationMutations.js — Pure functions for adjusting diarization breaks.
 *
 * These utilities handle repositioning speaker breaks when users drag
 * speaker labels to different word positions to correct diarization errors.
 *
 * See: docs/features/document-system/transcript-improvements.md
 */

/**
 * Deep clone utterances array for safe mutation.
 * @param {Array} utterances - Original utterances from richData
 * @returns {Array} Deep cloned copy
 */
export const cloneUtterances = (utterances) => {
  return JSON.parse(JSON.stringify(utterances));
};

/**
 * Recalculate an utterance's transcript string and timestamps from its words.
 * @param {Object} utterance - Utterance to update
 * @returns {Object} Updated utterance (mutated in place)
 */
export const recalculateUtterance = (utterance) => {
  if (!utterance.words || utterance.words.length === 0) {
    utterance.transcript = "";
    utterance.start = 0;
    utterance.end = 0;
    return utterance;
  }

  utterance.transcript = utterance.words.map((w) => w.word).join(" ");
  utterance.start = utterance.words[0].start;
  utterance.end = utterance.words[utterance.words.length - 1].end;

  return utterance;
};

/**
 * Build a flat list of all words with their utterance index and word index.
 * Useful for finding global word positions.
 * @param {Array} utterances - Utterances array
 * @returns {Array<{word: Object, uttIndex: number, wordIndex: number}>}
 */
export const flattenWords = (utterances) => {
  const result = [];
  for (let ui = 0; ui < utterances.length; ui++) {
    const utt = utterances[ui];
    if (utt.words) {
      for (let wi = 0; wi < utt.words.length; wi++) {
        result.push({
          word: utt.words[wi],
          uttIndex: ui,
          wordIndex: wi,
          speaker: utt.speaker,
        });
      }
    }
  }
  return result;
};

/**
 * Flatten a bounded utterance range into word entries with speaker assignments.
 * Keeps work scoped to the changed region so large transcripts stay responsive.
 */
const flattenUtteranceRange = (utterances, startUttIndex, endUttIndex) => {
  const flatWords = [];
  const offsetsByUtterance = {};

  for (let ui = startUttIndex; ui <= endUttIndex; ui++) {
    offsetsByUtterance[ui] = flatWords.length;
    const utt = utterances[ui];
    if (!utt?.words) continue;

    for (let wi = 0; wi < utt.words.length; wi++) {
      flatWords.push({
        word: utt.words[wi],
        speaker: utt.speaker,
      });
    }
  }

  return { flatWords, offsetsByUtterance };
};

/**
 * Rebuild just a bounded utterance range and stitch it back with untouched refs.
 */
const rebuildLocalizedRange = (utterances, startUttIndex, endUttIndex, flatWords) => {
  const rebuiltRange = rebuildFromWords(
    flatWords.map((fw) => ({
      ...fw.word,
      speaker: fw.speaker,
    }))
  );

  return [
    ...utterances.slice(0, startUttIndex),
    ...rebuiltRange,
    ...utterances.slice(endUttIndex + 1),
  ];
};

/**
 * Rebuild utterances from a flat word list with speaker assignments.
 * Groups consecutive words by speaker into utterances.
 * @param {Array} flatWords - Array of word objects with speaker field
 * @returns {Array} New utterances array
 */
export const rebuildUtterances = (flatWords) => {
  if (flatWords.length === 0) return [];

  const utterances = [];
  let currentUtt = {
    speaker: flatWords[0].speaker,
    words: [],
    transcript: "",
    start: 0,
    end: 0,
  };

  for (const fw of flatWords) {
    if (fw.speaker !== currentUtt.speaker) {
      // Speaker changed - finalize current utterance, start new one
      recalculateUtterance(currentUtt);
      utterances.push(currentUtt);
      currentUtt = {
        speaker: fw.speaker,
        words: [],
        transcript: "",
        start: 0,
        end: 0,
      };
    }
    currentUtt.words.push({ ...fw.word, speaker: fw.speaker });
  }

  // Don't forget the last utterance
  recalculateUtterance(currentUtt);
  utterances.push(currentUtt);

  return utterances;
};

/**
 * Insert a speaker break at a specific word position.
 * The speaker label was dropped before this word, so from this word forward
 * (until the next speaker change), the content belongs to the dropped speaker.
 *
 * @param {Array} utterances - Cloned utterances array
 * @param {number} uttIndex - Utterance index containing the target word
 * @param {number} wordIndex - Word index where the break should be inserted
 * @param {number} newSpeaker - Speaker index to assign from this word forward
 * @returns {Array} Rebuilt utterances array
 */
export const insertSpeakerBreak = (utterances, uttIndex, wordIndex, newSpeaker) => {
  // Flatten all words
  const flatWords = flattenWords(utterances);

  // Calculate global word index
  let globalIndex = 0;
  for (let ui = 0; ui < uttIndex; ui++) {
    globalIndex += utterances[ui].words?.length || 0;
  }
  globalIndex += wordIndex;

  // Find how far forward to apply the new speaker
  // We apply until we hit a word that already has a different speaker change
  // Or to the end of what was the current utterance's span
  const originalSpeaker = flatWords[globalIndex]?.speaker;
  
  // Apply new speaker from globalIndex forward until we hit a different original speaker
  for (let i = globalIndex; i < flatWords.length; i++) {
    if (flatWords[i].speaker !== originalSpeaker) {
      break; // Stop at existing speaker boundary
    }
    flatWords[i].speaker = newSpeaker;
  }

  // Rebuild utterances from modified flat list
  return rebuildUtterances(flatWords.map(fw => ({
    ...fw.word,
    speaker: fw.speaker,
  })).map((w, i) => ({ word: w, speaker: flatWords[i].speaker })));
};

/**
 * Simpler version: reassign speaker for a range of words.
 * @param {Array} utterances - Cloned utterances array
 * @param {number} uttIndex - Target utterance
 * @param {number} fromWordIndex - Start word (inclusive)
 * @param {number} newSpeaker - New speaker index
 * @returns {Array} Rebuilt utterances
 */
export const reassignFromWord = (utterances, uttIndex, fromWordIndex, newSpeaker) => {
  const flatWords = flattenWords(utterances);

  // Calculate global word index
  let globalIndex = 0;
  for (let ui = 0; ui < uttIndex; ui++) {
    globalIndex += utterances[ui].words?.length || 0;
  }
  globalIndex += fromWordIndex;

  if (globalIndex >= flatWords.length) return utterances;

  const originalSpeaker = flatWords[globalIndex].speaker;
  
  // If dropping same speaker, no change needed
  if (originalSpeaker === newSpeaker) return utterances;

  // Reassign from this word to end of original speaker's segment
  for (let i = globalIndex; i < flatWords.length; i++) {
    if (flatWords[i].speaker !== originalSpeaker) break;
    flatWords[i].speaker = newSpeaker;
  }

  // Rebuild - group consecutive same-speaker words into utterances
  const rebuiltWords = flatWords.map(fw => ({
    ...fw.word,
    speaker: fw.speaker,
  }));

  return rebuildFromWords(rebuiltWords);
};

/**
 * Localized variant of reassignFromWord used by speaker-label drag/drop.
 * Only rebuilds source +/- 1 utterance to allow row memoization to skip
 * unaffected transcript rows.
 */
export const reassignFromWordLocalized = (utterances, uttIndex, fromWordIndex, newSpeaker) => {
  if (!Array.isArray(utterances) || !utterances[uttIndex]) return utterances;

  const sourceWords = utterances[uttIndex].words || [];
  if (fromWordIndex < 0 || fromWordIndex >= sourceWords.length) return utterances;

  const startUttIndex = Math.max(0, uttIndex - 1);
  const endUttIndex = Math.min(utterances.length - 1, uttIndex + 1);
  const { flatWords, offsetsByUtterance } = flattenUtteranceRange(
    utterances,
    startUttIndex,
    endUttIndex
  );

  const localStart = (offsetsByUtterance[uttIndex] || 0) + fromWordIndex;
  if (localStart >= flatWords.length) return utterances;

  const originalSpeaker = flatWords[localStart].speaker;
  if (originalSpeaker === newSpeaker) return utterances;

  for (let i = localStart; i < flatWords.length; i++) {
    if (flatWords[i].speaker !== originalSpeaker) break;
    flatWords[i].speaker = newSpeaker;
  }

  return rebuildLocalizedRange(utterances, startUttIndex, endUttIndex, flatWords);
};

/**
 * Reassign a specific range of words to a new speaker.
 * Used when user selects text and assigns it to a speaker.
 * 
 * @param {Array} utterances - Cloned utterances array
 * @param {number} startUttIndex - Starting utterance index
 * @param {number} startWordIndex - Starting word index within that utterance
 * @param {number} endUttIndex - Ending utterance index
 * @param {number} endWordIndex - Ending word index within that utterance (inclusive)
 * @param {number} newSpeaker - Speaker index to assign
 * @returns {Array} Rebuilt utterances
 */
export const reassignWordRange = (utterances, startUttIndex, startWordIndex, endUttIndex, endWordIndex, newSpeaker) => {
  const flatWords = flattenWords(utterances);

  // Calculate global start index
  let globalStart = 0;
  for (let ui = 0; ui < startUttIndex; ui++) {
    globalStart += utterances[ui].words?.length || 0;
  }
  globalStart += startWordIndex;

  // Calculate global end index (inclusive)
  let globalEnd = 0;
  for (let ui = 0; ui < endUttIndex; ui++) {
    globalEnd += utterances[ui].words?.length || 0;
  }
  globalEnd += endWordIndex;

  if (globalStart >= flatWords.length) return utterances;
  globalEnd = Math.min(globalEnd, flatWords.length - 1);

  // Reassign the selected range
  for (let i = globalStart; i <= globalEnd; i++) {
    flatWords[i].speaker = newSpeaker;
  }

  // Rebuild utterances
  const rebuiltWords = flatWords.map(fw => ({
    ...fw.word,
    speaker: fw.speaker,
  }));

  return rebuildFromWords(rebuiltWords);
};

/**
 * Extend the previous speaker to include words up to a target position.
 * Used when dragging a speaker label DOWN (to a later position).
 * The words from the dragged label's start up to (but not including) the target
 * get reassigned to the speaker BEFORE the dragged utterance.
 *
 * @param {Array} utterances - Cloned utterances array
 * @param {number} sourceUttIndex - The utterance whose label was dragged
 * @param {number} targetUttIndex - Target utterance to extend previous speaker to
 * @param {number} targetWordIndex - Target word index (exclusive - speaker change happens here)
 * @returns {Array} Rebuilt utterances
 */
export const extendPreviousSpeakerTo = (utterances, sourceUttIndex, targetUttIndex, targetWordIndex) => {
  // Need a previous speaker to extend
  if (sourceUttIndex === 0) return utterances;

  const flatWords = flattenWords(utterances);

  // Find where source utterance starts in flat list
  let sourceGlobalStart = 0;
  for (let ui = 0; ui < sourceUttIndex; ui++) {
    sourceGlobalStart += utterances[ui].words?.length || 0;
  }

  // Find target position in flat list
  let targetGlobalIndex = 0;
  for (let ui = 0; ui < targetUttIndex; ui++) {
    targetGlobalIndex += utterances[ui].words?.length || 0;
  }
  targetGlobalIndex += targetWordIndex;

  if (targetGlobalIndex <= sourceGlobalStart) return utterances;

  // Get the previous speaker (speaker before the source utterance)
  const previousSpeaker = utterances[sourceUttIndex - 1].speaker;

  // Reassign words from source start up to (but not including) target to previous speaker
  for (let i = sourceGlobalStart; i < targetGlobalIndex && i < flatWords.length; i++) {
    flatWords[i].speaker = previousSpeaker;
  }

  // Rebuild utterances
  const rebuiltWords = flatWords.map(fw => ({
    ...fw.word,
    speaker: fw.speaker,
  }));

  return rebuildFromWords(rebuiltWords);
};

/**
 * Localized variant of extendPreviousSpeakerTo used by drag-down behavior.
 * Rebuilds only the impacted region (source-1 through target+1).
 */
export const extendPreviousSpeakerToLocalized = (
  utterances,
  sourceUttIndex,
  targetUttIndex,
  targetWordIndex
) => {
  if (!Array.isArray(utterances) || sourceUttIndex <= 0) return utterances;
  if (!utterances[sourceUttIndex] || !utterances[targetUttIndex]) return utterances;

  const startUttIndex = Math.max(0, sourceUttIndex - 1);
  const endUttIndex = Math.min(utterances.length - 1, targetUttIndex + 1);
  const { flatWords, offsetsByUtterance } = flattenUtteranceRange(
    utterances,
    startUttIndex,
    endUttIndex
  );

  const sourceGlobalStart = offsetsByUtterance[sourceUttIndex] || 0;
  const targetBase = offsetsByUtterance[targetUttIndex] || flatWords.length;
  const targetGlobalIndex = Math.min(flatWords.length, targetBase + Math.max(0, targetWordIndex));

  if (targetGlobalIndex <= sourceGlobalStart) return utterances;

  const previousSpeaker = utterances[sourceUttIndex - 1].speaker;
  for (let i = sourceGlobalStart; i < targetGlobalIndex && i < flatWords.length; i++) {
    flatWords[i].speaker = previousSpeaker;
  }

  return rebuildLocalizedRange(utterances, startUttIndex, endUttIndex, flatWords);
};

/**
 * Rebuild utterances from a flat array of words (each with speaker field).
 * @param {Array} words - Flat word array
 * @returns {Array} New utterances
 */
export const rebuildFromWords = (words) => {
  if (words.length === 0) return [];

  const utterances = [];
  let currentUtt = {
    speaker: words[0].speaker,
    words: [],
    transcript: "",
    start: 0,
    end: 0,
  };

  for (const w of words) {
    if (w.speaker !== currentUtt.speaker) {
      recalculateUtterance(currentUtt);
      utterances.push(currentUtt);
      currentUtt = {
        speaker: w.speaker,
        words: [],
        transcript: "",
        start: 0,
        end: 0,
      };
    }
    currentUtt.words.push(w);
  }

  recalculateUtterance(currentUtt);
  utterances.push(currentUtt);

  return utterances;
};

// ----- Legacy functions (kept for potential future use) -----

/**
 * Move words across a break boundary between two adjacent utterances.
 * @deprecated Use reassignFromWord instead
 */
export const moveWordsAcrossBreak = (utterances, breakIndex, wordOffset) => {
  if (wordOffset === 0) return utterances;

  const firstUtt = utterances[breakIndex];
  const secondUtt = utterances[breakIndex + 1];

  if (!firstUtt || !secondUtt) return utterances;

  const firstWords = firstUtt.words || [];
  const secondWords = secondUtt.words || [];

  if (wordOffset > 0) {
    const moveCount = Math.min(wordOffset, firstWords.length - 1);
    if (moveCount <= 0) return utterances;

    const wordsToMove = firstWords.splice(-moveCount);
    wordsToMove.forEach((w) => {
      w.speaker = secondUtt.speaker;
    });
    secondUtt.words = [...wordsToMove, ...secondWords];
  } else {
    const moveCount = Math.min(-wordOffset, secondWords.length - 1);
    if (moveCount <= 0) return utterances;

    const wordsToMove = secondWords.splice(0, moveCount);
    wordsToMove.forEach((w) => {
      w.speaker = firstUtt.speaker;
    });
    firstUtt.words = [...firstWords, ...wordsToMove];
  }

  recalculateUtterance(firstUtt);
  recalculateUtterance(secondUtt);

  if (firstUtt.speaker === secondUtt.speaker) {
    return mergeUtterances(utterances, breakIndex);
  }

  return utterances;
};

/**
 * Merge two adjacent utterances into one.
 */
export const mergeUtterances = (utterances, firstIndex) => {
  const first = utterances[firstIndex];
  const second = utterances[firstIndex + 1];

  if (!first || !second) return utterances;

  first.words = [...(first.words || []), ...(second.words || [])];
  recalculateUtterance(first);
  utterances.splice(firstIndex + 1, 1);

  return utterances;
};

/**
 * Find all break indices where speaker changes.
 */
export const findSpeakerBreaks = (utterances) => {
  const breaks = [];
  for (let i = 0; i < utterances.length - 1; i++) {
    if (utterances[i].speaker !== utterances[i + 1].speaker) {
      breaks.push(i);
    }
  }
  return breaks;
};

/**
 * Calculate how many words can be moved in each direction at a break.
 */
export const getMoveLimits = (utterances, breakIndex) => {
  const first = utterances[breakIndex];
  const second = utterances[breakIndex + 1];

  if (!first || !second) {
    return { maxForward: 0, maxBackward: 0 };
  }

  return {
    maxForward: Math.max(0, (first.words?.length || 0) - 1),
    maxBackward: Math.max(0, (second.words?.length || 0) - 1),
  };
};

/**
 * Validate utterances structure before save.
 */
export const validateUtterances = (utterances) => {
  if (!Array.isArray(utterances)) {
    return { valid: false, error: "Utterances must be an array" };
  }

  for (let i = 0; i < utterances.length; i++) {
    const utt = utterances[i];
    if (!utt.words || utt.words.length === 0) {
      return { valid: false, error: `Utterance ${i} has no words` };
    }
    if (utt.speaker === undefined || utt.speaker === null) {
      return { valid: false, error: `Utterance ${i} has no speaker` };
    }
  }

  return { valid: true };
};
